// Parse the member literals out of a MySQL inline `ENUM(...)` / `SET(...)`
// column type string. MySQL is the only one of the five engines whose native
// enumeration members travel inline in the column type (`information_schema`
// `COLUMN_TYPE` is `enum('G','PG','PG-13','R','NC-17')`), so the resolver can
// surface them synchronously from the schema-bus snapshot without the
// MetaInfo IPC round-trip the Enum feature uses for declared columns.
//
// pgsql native enums are user-defined types (`dataType` is the type *name*,
// members live in `pg_enum`); Oracle / MSSQL / SQLite have no native enum type
// (they emulate via CHECK constraints). Those engines therefore return null
// here — their values flow through the MetaInfo cache instead.

import { parseEnumValues } from '@/features/sql-resolver/ddl/extract/type.parse';

const INLINE_ENUM_HEAD = /^\s*(?:enum|set)\s*\(/i;

/**
 * Returns the ordered member literals of a MySQL inline `ENUM(...)` / `SET(...)`
 * type, or `null` when `dataType` is not an inline enum/set (so callers can
 * fall through to other value sources). Honours SQL `''` → `'` escaping inside
 * member literals. Returns `null` for a malformed/empty member list.
 */
export function parseInlineEnumMembers(dataType: string): readonly string[] | null {
  if (!INLINE_ENUM_HEAD.test(dataType)) return null;
  const open = dataType.indexOf('(');
  if (open === -1) return null;
  const close = dataType.lastIndexOf(')');
  if (close <= open) return null;
  const values = parseEnumValues(dataType, open + 1, close);
  return values.length > 0 ? values : null;
}
